import React from 'react';

interface EmptyTableStateProps {
  /** Title for the empty state */
  title: string;
  /** Description text for the empty state */
  description: string;
  /** Icon to display - can be an SVG element or an emoji/unicode character */
  icon?: React.ReactNode;
  /** Primary action button */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  /** Secondary action button */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  /** Show when filters are applied but no results found */
  hasFilters?: boolean;
  /** Action to clear filters */
  onClearFilters?: () => void;
}

/**
 * Generic empty state component for tables
 * Based on the design pattern shown in the provided image with building icon
 */
export const EmptyTableState: React.FC<EmptyTableStateProps> = ({
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  hasFilters = false,
  onClearFilters,
}) => {
  // Default building icon matching the image design
  const defaultIcon = (
    <div className="mx-auto mb-6">
      <svg className="h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    </div>
  );

  // If filters are applied, show filter-specific message
  if (hasFilters) {
    return (
      <div className="text-center py-16">
        <div className="mb-6">
          <svg className="mx-auto w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No items match your filters</h3>
        <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
          Try adjusting your search criteria or filters to find what you're looking for.
        </p>
        {onClearFilters && (
          <button className="access-btn access-secondary" onClick={onClearFilters}>
            Clear all filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="text-center py-16">
      {/* Icon - use provided icon or default building icon */}
      {icon || defaultIcon}
      
      {/* Title */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      
      {/* Description */}
      <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
        {description}
      </p>
      
      {/* Actions */}
      {(primaryAction || secondaryAction) && (
        <div className="flex justify-center gap-3">
          {primaryAction && (
            <button className="access-btn access-primary" onClick={primaryAction.onClick}>
              {primaryAction.icon && <span className="mr-2">{primaryAction.icon}</span>}
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button className="access-btn access-secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.icon && <span className="mr-2">{secondaryAction.icon}</span>}
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Loading skeleton component for tables
export const TableLoadingSkeleton: React.FC<{
  rows?: number;
  columns?: number;
}> = ({ rows = 5, columns = 9 }) => (
  <div className="table-container">
    <div className="p-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-4">
        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
        <div className="h-8 bg-gray-200 rounded w-24 animate-pulse"></div>
      </div>
      
      {/* Table skeleton rows */}
      <div className="space-y-3">
        {[...Array(rows)].map((_, index) => (
          <div key={index} className="flex items-center space-x-4 py-3">
            {[...Array(columns)].map((_, colIndex) => (
              <div 
                key={colIndex} 
                className={`h-4 bg-gray-200 rounded animate-pulse ${
                  colIndex === 0 ? 'flex-1' : // First column wider
                  colIndex === columns - 1 ? 'w-20' : // Last column (actions)
                  colIndex === columns - 2 ? 'w-16' : // Status column
                  'flex-1'
                }`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Error state component for tables
export const TableErrorState: React.FC<{
  onRetry?: () => void;
  error?: string;
  title?: string;
}> = ({ 
  onRetry, 
  error = "Failed to load data", 
  title = "Something went wrong"
}) => (
  <div className="text-center py-16">
    <div className="mb-6">
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
        <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
    </div>
    <h3 className="text-lg font-semibold text-red-900 mb-2">{title}</h3>
    <p className="text-red-600 text-sm mb-6 max-w-sm mx-auto">
      {error}. Please try again or contact support if the problem persists.
    </p>
    {onRetry && (
      <button className="access-btn access-primary" onClick={onRetry}>
        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Try Again
      </button>
    )}
  </div>
);
